import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Loading skeletons shared by every page.
 *
 * - `Skeleton` is the one bar primitive: a theme-aware tint (foreground at
 *   low opacity, so it reads on white cards, muted headers and dark mode
 *   alike) with a highlight sweeping across it. Reduced-motion users get a
 *   static bar.
 * - `SkeletonRegion` wraps a whole placeholder: it announces "Loading…" to
 *   screen readers once, hides the bars from them, and fades in after a
 *   short delay so a fast load never flashes a skeleton.
 * - The composites below (`TableSkeleton`, `PageSkeleton`, `SheetSkeleton`,
 *   …) mirror the portal's recurring layouts; page-specific skeletons build
 *   on `Skeleton` + `SkeletonRegion` rather than styling their own bars.
 */

interface SkeletonProps {
  className?: string;
  style?: React.CSSProperties;
}

export function Skeleton({ className, style }: SkeletonProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-md bg-foreground/[0.08]',
        'before:absolute before:inset-0 before:-translate-x-full before:animate-skeleton-sweep',
        'before:bg-gradient-to-r before:from-transparent before:via-background/60 before:to-transparent',
        'motion-reduce:before:hidden',
        className
      )}
      style={style}
      aria-hidden="true"
    />
  );
}

// Set inside a SkeletonRegion so nested composites (a TableSkeleton inside a
// PageSkeleton) don't announce "Loading…" twice or fade in twice
const InsideRegion = React.createContext(false);

export function SkeletonRegion({
  label = 'Loading…',
  className,
  children,
}: {
  label?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const nested = React.useContext(InsideRegion);
  if (nested) return <div className={className}>{children}</div>;
  return (
    <InsideRegion.Provider value={true}>
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'animate-skeleton-in motion-reduce:animate-none',
          className
        )}
      >
        <span className="sr-only">{label}</span>
        {children}
      </div>
    </InsideRegion.Provider>
  );
}

// Fixed (not random) widths so a skeleton doesn't jitter between renders
const TEXT_WIDTHS = ['92%', '78%', '85%', '70%', '88%', '74%'];

/** A few lines of paragraph text. */
export function SkeletonText({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          className="h-3.5"
          style={{ width: TEXT_WIDTHS[i % TEXT_WIDTHS.length] }}
        />
      ))}
    </div>
  );
}

/** A bordered card with a title and two lines of text. */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn('rounded-lg border border-border p-4', className)}>
      <Skeleton className="mb-3 h-5 w-2/3" />
      <SkeletonText lines={2} />
    </div>
  );
}

/** Rounded filter-chip stubs (status chips above a table). */
export function ChipsSkeleton({
  widths = [56, 72, 64, 80],
  className,
}: {
  widths?: number[];
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {widths.map((w, i) => (
        <Skeleton key={i} className="h-6 rounded-full" style={{ width: w }} />
      ))}
    </div>
  );
}

/** Underlined tab-bar stubs (the workflow-style sheet tabs). */
export function TabsSkeleton({
  widths = [64, 96, 80, 64, 96],
  className,
}: {
  widths?: number[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex gap-4 border-b border-border px-1 pb-2.5 pt-1',
        className
      )}
    >
      {widths.map((w, i) => (
        <Skeleton key={i} className="h-4" style={{ width: w }} />
      ))}
    </div>
  );
}

// Column stubs for a table row. Later columns drop out on narrower screens
// the same way the real tables hide them; per-row widths vary a little so
// the block reads as data rather than a grid of identical bars.
const COLUMN_CLASSES = [
  'flex-[2]',
  'flex-1',
  'hidden md:block flex-1',
  'hidden md:block flex-[1.5]',
  'hidden lg:block flex-1',
  'hidden xl:block flex-1',
];
const ROW_SCALE = [1, 0.8, 0.92, 0.7, 0.86, 0.76, 0.95, 0.82];

/** Table rows without the surrounding chrome. */
export function TableRowsSkeleton({
  rows = 10,
  columns = 5,
}: {
  rows?: number;
  columns?: number;
}) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <div
          key={r}
          className="flex items-center gap-6 border-b border-border/40 px-3 py-3 last:border-b-0"
        >
          {COLUMN_CLASSES.slice(0, columns).map((cls, c) => (
            <div key={c} className={cn('min-w-0', cls)}>
              <Skeleton
                className="h-3.5"
                style={{
                  width: `${Math.round(ROW_SCALE[(r + c * 3) % ROW_SCALE.length] * 100)}%`,
                }}
              />
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

/**
 * A data table: optional chips or tabs above, a header row and body rows on
 * desktop, card stubs on mobile (the V2 tables switch to cards below `sm`).
 * `mobileCards={0}` is for tables that stay tables (scrolling sideways) on
 * mobile: the table stub then shows at every width.
 */
export function TableSkeleton({
  rows = 10,
  columns = 5,
  mobileCards = 5,
  chips = false,
  tabs = false,
  className,
  label,
}: {
  rows?: number;
  columns?: number;
  mobileCards?: number;
  chips?: boolean;
  tabs?: boolean;
  className?: string;
  label?: string;
}) {
  return (
    <SkeletonRegion label={label} className={className}>
      {tabs && <TabsSkeleton />}
      {chips && <ChipsSkeleton className="mb-3" />}

      <div
        className={cn(
          'overflow-hidden border border-border',
          mobileCards > 0 && 'hidden sm:block',
          tabs ? 'rounded-b-lg border-t-0' : 'rounded-lg'
        )}
      >
        <div className="flex items-center gap-6 border-b border-border bg-muted/50 px-3 py-3">
          {COLUMN_CLASSES.slice(0, columns).map((cls, c) => (
            <div key={c} className={cn('min-w-0', cls)}>
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
        <TableRowsSkeleton rows={rows} columns={columns} />
      </div>

      {mobileCards > 0 && (
        <div className={cn('space-y-2 sm:hidden', tabs && 'mt-3')}>
          {Array.from({ length: mobileCards }, (_, i) => (
            <div key={i} className="rounded-lg border border-border p-3">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="mt-2 h-3 w-24" />
              <Skeleton className="mt-3 h-3 w-full" />
            </div>
          ))}
        </div>
      )}
    </SkeletonRegion>
  );
}

/** A page's title row: a serif heading on the left, controls on the right. */
export function PageHeaderSkeleton({
  actions = 2,
  className,
}: {
  actions?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3',
        className
      )}
    >
      <div className="space-y-2">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-3.5 w-72 max-w-full" />
      </div>
      <div className="flex items-center gap-2">
        {Array.from({ length: actions }, (_, i) => (
          <Skeleton
            key={i}
            className={cn('h-9', i === 0 ? 'w-56 max-w-[50vw]' : 'w-24')}
          />
        ))}
      </div>
    </div>
  );
}

/** Full page: title row plus a table — the default list-page layout. */
export function PageSkeleton({
  tabs = true,
  chips = false,
  rows = 8,
  className,
  label,
}: {
  tabs?: boolean;
  chips?: boolean;
  rows?: number;
  className?: string;
  label?: string;
}) {
  return (
    <SkeletonRegion label={label} className={cn('space-y-4', className)}>
      <PageHeaderSkeleton />
      <TableSkeleton tabs={tabs} chips={chips} rows={rows} />
    </SkeletonRegion>
  );
}

/** Detail-sheet body: labelled field groups. */
export function SheetSkeleton({
  sections = 5,
  className,
  label,
}: {
  sections?: number;
  className?: string;
  label?: string;
}) {
  return (
    <SkeletonRegion label={label} className={cn('space-y-5 p-4', className)}>
      {Array.from({ length: sections }, (_, i) => (
        <div key={i} className="space-y-2.5">
          <Skeleton className="h-4 w-32" />
          <div className="grid grid-cols-[8rem_1fr] items-center gap-x-4 gap-y-2.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3.5 w-3/4" />
          </div>
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** A stack of list rows (history, groups, members…). */
export function ListSkeleton({
  rows = 5,
  className,
  label,
}: {
  rows?: number;
  className?: string;
  label?: string;
}) {
  return (
    <SkeletonRegion
      label={label}
      className={cn(
        'divide-y divide-border/60 overflow-hidden rounded-md border border-border',
        className
      )}
    >
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
          <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton
              className="h-3.5"
              style={{ width: `${Math.round(ROW_SCALE[i % 8] * 60)}%` }}
            />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-3 w-16 shrink-0" />
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** A settings form: labelled inputs and a save button. */
export function FormSkeleton({
  fields = 4,
  className,
  label,
}: {
  fields?: number;
  className?: string;
  label?: string;
}) {
  return (
    <SkeletonRegion
      label={label}
      className={cn('max-w-2xl space-y-5 py-2', className)}
    >
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-9 w-full" />
        </div>
      ))}
      <Skeleton className="h-9 w-32" />
    </SkeletonRegion>
  );
}

/** A responsive grid of cards (favorite links, templates, spreadsheets). */
export function CardGridSkeleton({
  count = 6,
  className,
  label,
  cardClassName,
}: {
  count?: number;
  className?: string;
  label?: string;
  cardClassName?: string;
}) {
  return (
    <SkeletonRegion
      label={label}
      className={cn(
        'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3',
        className
      )}
    >
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className={cn(
            'space-y-3 rounded-lg border border-border bg-card p-4',
            cardClassName
          )}
        >
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
          <SkeletonText lines={2} />
        </div>
      ))}
    </SkeletonRegion>
  );
}

// Event-block stubs per column: [top %, height %] — fixed so columns differ
// from each other without jittering between renders
const TIME_BLOCKS: [number, number][][] = [
  [
    [8, 10],
    [34, 14],
    [62, 8],
  ],
  [
    [18, 12],
    [48, 18],
  ],
  [
    [4, 8],
    [26, 10],
    [70, 12],
  ],
  [
    [40, 14],
    [60, 8],
  ],
];

/**
 * A time grid: an hour gutter plus one column per day or staff member, with
 * a few event-block stubs (calendar day/week views, the appointments board).
 */
export function TimeGridSkeleton({
  columns = 1,
  hours = 10,
  className,
  label,
}: {
  columns?: number;
  hours?: number;
  className?: string;
  label?: string;
}) {
  return (
    <SkeletonRegion
      label={label}
      className={cn(
        'flex flex-col overflow-hidden rounded-lg border border-border bg-card',
        className
      )}
    >
      <div className="flex border-b border-border">
        <div className="w-16 shrink-0 border-r border-border/60" />
        {Array.from({ length: columns }, (_, c) => (
          <div
            key={c}
            className="flex min-w-[120px] flex-1 items-center gap-2 border-l border-border/60 px-2 py-2 first:border-l-0"
          >
            <Skeleton className="h-6 w-6 shrink-0 rounded-full" />
            <Skeleton className="h-3.5 w-20" />
          </div>
        ))}
      </div>
      <div className="flex flex-1">
        <div className="w-16 shrink-0 border-r border-border/60">
          {Array.from({ length: hours }, (_, h) => (
            <div key={h} className="flex h-14 justify-end px-2 pt-1">
              <Skeleton className="h-2.5 w-8" />
            </div>
          ))}
        </div>
        {Array.from({ length: columns }, (_, c) => (
          <div
            key={c}
            className="relative min-w-[120px] flex-1 border-l border-border/60 first:border-l-0"
            style={{ height: hours * 56 }}
          >
            {TIME_BLOCKS[c % TIME_BLOCKS.length].map(([top, height], i) => (
              <Skeleton
                key={i}
                className="absolute inset-x-1.5"
                style={{ top: `${top}%`, height: `${height}%` }}
              />
            ))}
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}
